import type { Field } from "@components/FieldList";
import type { RepairFact, RepairModule } from "@models/repairInformation";

/** Human labels of the five repair-information modules. */
export const MODULE_LABELS: Record<RepairModule, string> = {
  labour_times: "Labour times",
  technical_data: "Technical data",
  manuals: "Repair manuals",
  bulletins: "Service bulletins",
  maintenance: "Maintenance",
};

function present(value: unknown): value is string | number | boolean {
  return value !== null && value !== undefined && value !== "";
}

/** A time in hours as the catalogue publishes it ("2.40") -> "2.40 h"; missing -> null. */
export function hours(value: unknown): string | null {
  return present(value) ? `${value} h` : null;
}

function text(value: unknown): string | null {
  return present(value) ? String(value) : null;
}

/** "6,90" + "l" -> "6,90 l"; a missing unit leaves the value alone; missing -> null. */
export function valueWithUnit(fact: RepairFact): string | null {
  const value = fact.display ?? fact.value_display ?? fact.value;
  if (!present(value)) return null;
  return present(fact.unit) ? `${value} ${fact.unit}` : String(value);
}

/**
 * One fact as `label: value` fields; missing values are left out. Facts are
 * what the embedding service publishes with each posting (at most 8 per hit):
 * labour times and maintenance: work and time; technical data: value and unit;
 * manuals and bulletins: the document reference.
 */
export function factFields(module: RepairModule, fact: RepairFact): Field[] {
  const fields: [string, string | null][] = (() => {
    switch (module) {
      case "labour_times":
        return [
          // The catalogue's source base time. The combined value adds exclusive
          // operations and is an unverified candidate, so it is shown apart.
          ["Time", hours(fact.time_h)],
          ["Combined (candidate)", hours(fact.combined_time_h)],
          ["Qualifier", text(fact.qualifier)],
          ["Status", text(fact.time_status)],
          ["Work ID", text(fact.work_id)],
        ];
      case "maintenance":
        return [
          ["Time", hours(fact.time_h)],
          ["Work ID", text(fact.work_id)],
        ];
      case "technical_data":
        return [
          ["Value", valueWithUnit(fact)],
          ["Qualifier", text(fact.qualifier)],
          ["Remark", text(fact.remark)],
        ];
      case "manuals":
      case "bulletins":
        return [
          ["Document", text(fact.manual_id)],
          ["Variant", text(fact.qual_col_id)],
        ];
      default:
        return [];
    }
  })();
  return fields.filter((field): field is [string, string] => field[1] !== null);
}
